import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export interface ShiftFormValues {
  code: string
  name: string
  startTime: string
  endTime: string
  graceInMinutes: number
  breakMinutes: number
  fullDayMinutes: number
  halfDayMinutes: number
}

const EMPTY: ShiftFormValues = {
  code: '',
  name: '',
  startTime: '09:00',
  endTime: '18:00',
  graceInMinutes: 10,
  breakMinutes: 60,
  fullDayMinutes: 480,
  halfDayMinutes: 240,
}

/**
 * The same form creates and corrects.
 *
 * <p>A shift could be created and never corrected: its grace minutes, break and full-day
 * threshold are the numbers the whole attendance calculation turns on, and a typo in any of
 * them was permanent — the only way round it being a second shift and a re-roster of everybody
 * on the first. `initial` is what makes this an edit; the code stays editable because a shift
 * mistyped as GEN1 has to be fixable too.
 */
export function ShiftForm({
  onSubmit,
  onCancel,
  submitting,
  initial,
}: {
  onSubmit: (values: ShiftFormValues) => void
  onCancel: () => void
  submitting: boolean
  initial?: ShiftFormValues
}) {
  const [values, setValues] = useState<ShiftFormValues>(initial ?? EMPTY)

  function set<K extends keyof ShiftFormValues>(key: K, value: ShiftFormValues[K]) {
    setValues((current) => ({ ...current, [key]: value }))
  }

  // A shift that ends before it starts runs through midnight. Saying so up front stops
  // people "fixing" a night shift by typing 06:00–22:00 and inverting their own roster.
  const crossesMidnight = values.endTime <= values.startTime

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit(values)
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="shift-code">Code</Label>
          <Input
            id="shift-code"
            required
            maxLength={30}
            placeholder="GEN"
            value={values.code}
            onChange={(event) => set('code', event.target.value.toUpperCase())}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="shift-name">Name</Label>
          <Input
            id="shift-name"
            required
            placeholder="General shift"
            value={values.name}
            onChange={(event) => set('name', event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="shift-start">Starts</Label>
          <Input
            id="shift-start"
            type="time"
            required
            value={values.startTime}
            onChange={(event) => set('startTime', event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="shift-end">Ends</Label>
          <Input
            id="shift-end"
            type="time"
            required
            value={values.endTime}
            onChange={(event) => set('endTime', event.target.value)}
          />
          {crossesMidnight && (
            <p className="text-xs text-[var(--color-muted)]">
              Runs past midnight — the exit punch counts against the day the shift started.
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="shift-grace">Grace on arrival (minutes)</Label>
          <Input
            id="shift-grace"
            type="number"
            min={0}
            max={120}
            value={values.graceInMinutes}
            onChange={(event) => set('graceInMinutes', Number(event.target.value))}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="shift-break">Unpaid break (minutes)</Label>
          <Input
            id="shift-break"
            type="number"
            min={0}
            max={240}
            value={values.breakMinutes}
            onChange={(event) => set('breakMinutes', Number(event.target.value))}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="shift-full">Full day at (minutes worked)</Label>
          <Input
            id="shift-full"
            type="number"
            min={60}
            max={960}
            value={values.fullDayMinutes}
            onChange={(event) => set('fullDayMinutes', Number(event.target.value))}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="shift-half">Half day at (minutes worked)</Label>
          <Input
            id="shift-half"
            type="number"
            min={30}
            max={values.fullDayMinutes}
            value={values.halfDayMinutes}
            onChange={(event) => set('halfDayMinutes', Number(event.target.value))}
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : 'Save shift'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
